import { CAD_MASTERPLAN_STYLE } from '../../Map/styles/mapTheme';

export const CAD_ASPHALT_PATTERN_ID = 'cad-masterplan-asphalt';
export const CAD_GRASS_PATTERN_ID = 'cad-masterplan-grass';

const PATTERN_SIZE = 256;
const PATTERN_PIXEL_RATIO = 2;
const TREE_SPRITE_SIZE = 96;
const TREE_SPRITE_PIXEL_RATIO = 2;

/** @param {number} index */
export function cadTreeSpriteId(index) {
  return `cad-masterplan-tree-${index}`;
}

function parseThemeRgb(hex) {
  const raw = String(hex || '').replace('#', '');
  const body = raw.length === 3
    ? raw.split('').map((ch) => `${ch}${ch}`).join('')
    : raw.slice(0, 6);
  const n = parseInt(body, 16);
  if (!Number.isFinite(n)) return { r: 41, g: 45, b: 51 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mixRgb(a, b, t) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function rgba(rgb, alpha = 255) {
  return [rgb.r, rgb.g, rgb.b, alpha];
}

function cssRgb(rgb) {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

function putPixel(data, size, x, y, color) {
  const i = ((y * size) + x) * 4;
  data[i] = color[0];
  data[i + 1] = color[1];
  data[i + 2] = color[2];
  data[i + 3] = color[3];
}

function hashLattice(x, y, seed) {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 2246822519)) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Value noise con retícula periódica: el tile cierra con el de al lado. */
function valueNoise(x, y, period, seed) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - (2 * fx));
  const sy = fy * fy * (3 - (2 * fy));
  const wrap = (v) => ((v % period) + period) % period;
  const x1 = wrap(x0 + 1);
  const y1 = wrap(y0 + 1);
  const wx0 = wrap(x0);
  const wy0 = wrap(y0);
  const n00 = hashLattice(wx0, wy0, seed);
  const n10 = hashLattice(x1, wy0, seed);
  const n01 = hashLattice(wx0, y1, seed);
  const n11 = hashLattice(x1, y1, seed);
  const top = n00 + ((n10 - n00) * sx);
  const bottom = n01 + ((n11 - n01) * sx);
  return top + ((bottom - top) * sy);
}

/** @param {number} u 0..1 @param {number} v 0..1 */
function fbm(u, v, baseFrequency, octaves, seed) {
  let amplitude = 1;
  let frequency = baseFrequency;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i += 1) {
    sum += valueNoise(u * frequency, v * frequency, frequency, seed + i) * amplitude;
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return norm > 0 ? sum / norm : 0;
}

function createCanvas(size) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  return { canvas, ctx };
}

function createPatternImageData(paint) {
  const surface = createCanvas(PATTERN_SIZE);
  if (!surface) return null;
  const image = surface.ctx.createImageData(PATTERN_SIZE, PATTERN_SIZE);
  paint(image.data, PATTERN_SIZE);
  surface.ctx.putImageData(image, 0, 0);
  return surface.ctx.getImageData(0, 0, PATTERN_SIZE, PATTERN_SIZE);
}

function paintAsphalt(data, size) {
  const base = parseThemeRgb(CAD_MASTERPLAN_STYLE.streetFill);
  const speck = parseThemeRgb(CAD_MASTERPLAN_STYLE.sidewalkColor);
  const dark = parseThemeRgb(CAD_MASTERPLAN_STYLE.streetShadowFill);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      // Manchones de carpeta + grano fino: sin el fbm el asfalto es un plano.
      const patch = fbm(u, v, 4, 3, 11) - 0.5;
      const grain = fbm(u, v, 32, 2, 71) - 0.5;
      let tone = patch < 0
        ? mixRgb(base, dark, Math.min(0.32, -patch * 0.5))
        : mixRgb(base, speck, Math.min(0.14, patch * 0.22));
      tone = grain < 0
        ? mixRgb(tone, dark, Math.min(0.2, -grain * 0.35))
        : mixRgb(tone, speck, Math.min(0.12, grain * 0.2));
      // Áridos sueltos: pocos píxeles muy claros, es lo que da textura al zoom.
      if (hashLattice(x, y, 991) > 0.985) tone = mixRgb(tone, speck, 0.4);
      putPixel(data, size, x, y, rgba(tone));
    }
  }
}

function paintGrass(data, size) {
  const base = parseThemeRgb(CAD_MASTERPLAN_STYLE.greenFill);
  const dark = parseThemeRgb(CAD_MASTERPLAN_STYLE.treeStroke);
  const light = parseThemeRgb(CAD_MASTERPLAN_STYLE.sidewalkColor);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      const patch = fbm(u, v, 3, 3, 23) - 0.5;
      const blades = fbm(u * 0.35, v, 24, 2, 53) - 0.5;
      let tone = patch < 0
        ? mixRgb(base, dark, Math.min(0.32, -patch * 0.48))
        : mixRgb(base, light, Math.min(0.1, patch * 0.18));
      tone = blades < 0
        ? mixRgb(tone, dark, Math.min(0.2, -blades * 0.32))
        : mixRgb(tone, light, Math.min(0.08, blades * 0.14));
      putPixel(data, size, x, y, rgba(tone));
    }
  }
}

function paintBlobs(ctx, cx, cy, radius, count, phase) {
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.62, 0, Math.PI * 2);
  for (let i = 0; i < count; i += 1) {
    const angle = ((i / count) * Math.PI * 2) + phase;
    const spread = radius * 0.46;
    const lobe = radius * (0.38 + (0.1 * Math.sin((i * 2.7) + phase)));
    ctx.moveTo(cx + (Math.cos(angle) * spread) + lobe, cy + (Math.sin(angle) * spread));
    ctx.arc(cx + (Math.cos(angle) * spread), cy + (Math.sin(angle) * spread), lobe, 0, Math.PI * 2);
  }
  ctx.fill();
}

/**
 * Copa vista desde arriba: base oscura corrida al sur, más oscura que la plaza.
 */
function createTreeSpriteImageData(variant) {
  const surface = createCanvas(TREE_SPRITE_SIZE);
  if (!surface) return null;
  const { ctx } = surface;
  const green = parseThemeRgb(CAD_MASTERPLAN_STYLE.treeCanopy);
  const dark = mixRgb(green, parseThemeRgb(CAD_MASTERPLAN_STYLE.treeStroke), 0.58);
  const light = mixRgb(green, parseThemeRgb(CAD_MASTERPLAN_STYLE.sidewalkColor), 0.28);
  const center = TREE_SPRITE_SIZE / 2;
  const radius = TREE_SPRITE_SIZE * (variant === 3 ? 0.34 : 0.4);
  const phase = variant * 0.85;
  const lobes = 4 + (variant % 4);

  ctx.clearRect(0, 0, TREE_SPRITE_SIZE, TREE_SPRITE_SIZE);
  ctx.fillStyle = cssRgb(dark);
  paintBlobs(ctx, center, center + (radius * 0.22), radius, lobes, phase);
  ctx.fillStyle = cssRgb(green);
  paintBlobs(ctx, center, center - (radius * 0.06), radius * 0.88, lobes, phase + 0.4);
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = cssRgb(light);
  paintBlobs(ctx, center, center - (radius * 0.3), radius * 0.42, lobes, phase + 1.1);
  ctx.globalAlpha = 1;

  return ctx.getImageData(0, 0, TREE_SPRITE_SIZE, TREE_SPRITE_SIZE);
}

function upsertImage(map, id, image, pixelRatio) {
  if (!map || !image) return false;
  try {
    if (map.hasImage?.(id)) return true;
    map.addImage(id, image, { pixelRatio });
    return true;
  } catch {
    return false;
  }
}

/**
 * Texturas de asfalto / césped (`fill-pattern`) y sprites de copa
 * (`icon-image`). Se generan una sola vez por mapa: después es todo GPU.
 *
 * @param {import('maplibre-gl').Map|null} map
 * @returns {{ asphalt: boolean, grass: boolean, trees: boolean }}
 */
export function ensureCadMasterplanPatternImages(map) {
  if (!map || typeof map.addImage !== 'function') {
    return { asphalt: false, grass: false, trees: false };
  }
  const asphalt = upsertImage(
    map,
    CAD_ASPHALT_PATTERN_ID,
    createPatternImageData(paintAsphalt),
    PATTERN_PIXEL_RATIO,
  );
  const grass = upsertImage(
    map,
    CAD_GRASS_PATTERN_ID,
    createPatternImageData(paintGrass),
    PATTERN_PIXEL_RATIO,
  );
  const variants = CAD_MASTERPLAN_STYLE.treeVariants || 0;
  let trees = variants > 0;
  for (let i = 0; i < variants; i += 1) {
    const ok = upsertImage(
      map,
      cadTreeSpriteId(i),
      createTreeSpriteImageData(i),
      TREE_SPRITE_PIXEL_RATIO,
    );
    trees = trees && ok;
  }
  return { asphalt, grass, trees };
}
